import React, { useEffect, useState, useContext } from "react"
import { HiOutlineRefresh, HiOutlineLocationMarker, HiOutlineSearch, HiOutlineClock } from "react-icons/hi"
import { post_middleware_osrm_search } from "services/middleware_services"
import { format_datetime_by_local_client } from "helpers/datetime_helper"
import AuthContext from "store/context/PublicContext"

interface LatestMovementsWidgetProps {
  title?: string
  actions?: string[]
  sortField?: string
  heightClass?: string
}

const LatestMovementsWidget: React.FC<LatestMovementsWidgetProps> = ({
  title = "Últimos Movimientos",
  actions,
  sortField = "_omnixStateMachine.currentState.date",
  heightClass = "h-full"
}) => {
  const [storeFilter, setStoreFilter] = useState("")
  const [debouncedStoreFilter, setDebouncedStoreFilter] = useState("")
  const [records, setRecords] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const { user: { token } } = useContext(AuthContext)

  // Native React debounce effect
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedStoreFilter(storeFilter)
    }, 500)

    return () => {
      clearTimeout(handler)
    }
  }, [storeFilter])

  const fetchLatest = async () => {
    let filters: Record<string, any> = {}
    
    if (debouncedStoreFilter.trim()) {
      try {
        const sourceRes = await post_middleware_osrm_search(
          "sources",
          { from: 0, size: 200, filters: { id: debouncedStoreFilter.trim() } },
          token
        )
        const matchedSources = sourceRes?.data?.message?.records || []
        const matchedCodVTASAs = matchedSources.map((s: any) => s?.custom?.codVTASA).filter(Boolean)
        
        if (matchedCodVTASAs.length > 0) {
          filters.almacen = String(matchedCodVTASAs[0]) // Pass the first matched codVTASA strictly as a string
        } else {
          filters.almacen = "NOT_FOUND_DUMMY_ID_OIM" 
        }
      } catch (e) {
        filters.almacen = "NOT_FOUND_DUMMY_ID_OIM"
      }
    }

    if (actions && actions.length > 0) {
      // Pass array directly — OSRM normalizes arrays to $in automatically
      filters["omnixMetadata.currentState.action"] = actions.length === 1 ? actions[0] : actions
    }

    setIsLoading(true)
    try {
      const res = await post_middleware_osrm_search(
        "series",
        {
          from: 0,
          size: 15,
          filters,
          sortBy: { [sortField]: "desc" } // ElasticSearch descending
        },
        token
      )
      setRecords(res?.data?.message?.records || [])
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setRecords([]) // Silent 404
      } else {
        console.error("Error fetching latest series movements", err)
      }
    } finally {
      setIsLoading(false)
    }
  }

  // Fetch when the debounced search changes OR on first mount per widget instance
  useEffect(() => {
    fetchLatest()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedStoreFilter, JSON.stringify(actions), sortField])

  return (
    <div className={`w-full flex justify-center items-start pt-0 flex-1 min-h-0 ${heightClass}`}>
      <div className="w-full bg-white rounded-none border-t xl:border-t-0 xl:border-l border-gray-100 shadow-sm flex flex-col h-full xl:border-b" style={{ maxHeight: "100%" }}>
        
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100 flex flex-row items-center justify-between bg-white relative z-10">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse"></div>
            <h3 className="text-[13px] font-black text-gray-800 uppercase tracking-widest">
              {title}
            </h3>
          </div>
          <button 
            onClick={fetchLatest}
            disabled={isLoading}
            className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
          >
            <HiOutlineRefresh className={`text-[15px] ${isLoading ? "animate-spin text-indigo-400" : ""}`} />
          </button>
        </div>

        {/* Filter */}
        <div className="p-4 bg-gray-50/50 border-b border-gray-100">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <HiOutlineLocationMarker className="text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-9 pr-3 py-2 text-[12.5px] border border-gray-200 rounded-lg bg-white shadow-sm focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 placeholder-gray-400 transition-all"
              placeholder="Filtrar por Tienda (Ej: 9001)..."
              value={storeFilter}
              onChange={(e) => setStoreFilter(e.target.value)}
            />
          </div>
        </div>

        {/* List Content */}
        <div className="flex-1 overflow-y-auto w-full p-0 flex flex-col hide-scrollbar">
          {isLoading && records.length === 0 ? (
            <div className="p-8 flex flex-col items-center justify-center gap-3">
              <span className="h-5 w-5 border-2 border-gray-300 border-t-indigo-600 rounded-full animate-spin"></span>
              <p className="text-[12px] font-medium text-gray-400">Cargando datos...</p>
            </div>
          ) : records.length > 0 ? (
            <div className="flex flex-col divide-y divide-gray-50">
              {records.map((item: any, idx: number) => {
                const dateStr = item?._omnixMetadata?.updatedAt || item?._omnixMetadata?.createdAt
                const fechaFormat = dateStr 
                  ? format_datetime_by_local_client(new Date(parseInt(dateStr)), "dd MMM, HH:mm") 
                  : "--"
                const estado = item?.estadoOmnix || "Estado Desconocido"
                const usuario = item?.omnixMetadata?.currentState?.email || item?.omnixMetadata?.currentState?.user || "system"
                const accion = item?.omnixMetadata?.currentState?.action

                return (
                  <div key={idx} className="p-4 hover:bg-gray-50/80 transition-colors group">
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <div className="flex flex-col min-w-0">
                        <span className="text-[13px] font-bold text-gray-800 truncate">
                          {item.serieSap || item.sku}
                        </span>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[11px] font-semibold text-indigo-500 truncate">
                            SKU: {item.sku}
                          </span>
                          <span className={`px-1.5 py-[1px] text-[9px] font-black rounded uppercase tracking-wider ${
                            accion === "CREATE" ? "bg-emerald-100 text-emerald-700" :
                            accion === "BULK_UPDATE" ? "bg-amber-100 text-amber-700" :
                            "bg-blue-100 text-blue-700"
                          }`}>
                            {accion}
                          </span>
                        </div>
                      </div>
                      <div className="shrink-0 flex items-center gap-1 bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide">
                        {item.almacen || "N/A"}
                      </div>
                    </div>
                    
                    <div className="bg-white border border-gray-100 shadow-sm rounded p-2.5 mt-2 flex flex-col gap-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-[11.5px] font-semibold text-gray-700">
                          {estado}
                        </span>
                        <div className="flex items-center gap-1 text-[10px] font-medium text-gray-400">
                          <HiOutlineClock className="text-[12px]" />
                          {fechaFormat}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 text-[10.5px] font-medium text-gray-500 p-1 bg-gray-50 rounded">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-400"></span>
                        {usuario}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="p-8 flex flex-col items-center justify-center gap-2 h-full text-center">
              <HiOutlineSearch className="text-3xl text-gray-300" />
              <p className="text-[13px] font-bold text-gray-500">Sin movimientos</p>
              <p className="text-[11px] font-medium text-gray-400 max-w-[200px]">
                No hay actividad reciente{debouncedStoreFilter ? " para esta tienda" : ""}.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default LatestMovementsWidget
